/**
 * Subida de fotos: elegir, quitar, ordenar y marcar la principal
 * -----------------------------------------------------------------------------
 * El `<input type="file">` de serie hace una sola cosa: elegir. No deja quitar
 * una de las cuatro que se marcaron, no deja decir cuál va primera y su botón
 * es el que pinte cada navegador — distinto en cada uno y ajeno al resto de la
 * pantalla.
 *
 * Este componente envuelve ese input y le pone encima lo que falta.
 *
 *   <div data-company-fotos data-company-fotos-campo="fotos">
 *     <input type="file" name="fotos[]" multiple hidden>
 *     <input type="hidden" name="fotos_plan">
 *     <template data-company-fotos-guardadas>
 *       [{"id":"…","url":"…","nombre":"…","principal":true}]
 *     </template>
 *   </div>
 *
 * ## Las guardadas y las nuevas, en la misma rejilla
 *
 * Y no en dos bloques. Quien ordena no piensa «estas ya están y estas no»:
 * piensa cuál va primera. Con dos listas, arrastrar una nueva delante de una
 * guardada no se puede expresar.
 *
 * ## Qué se manda al servidor
 *
 * Un solo campo con el plan, en JSON:
 *
 *   { "orden": ["id-1", "nueva-0", "id-2"], "principal": "nueva-0", "quitar": ["id-3"] }
 *
 * Un campo y no tres listas paralelas porque el orden, la principal y lo que se
 * quita **son la misma decisión**: mandados por separado pueden llegar
 * contradiciéndose -una principal que además está en «quitar»- y el servidor
 * tendría que arbitrar entre dos verdades.
 *
 * Los ficheros siguen viajando por el `<input type="file">`, reconstruido para
 * que su contenido y los `nueva-N` del plan digan lo mismo.
 */

import { aWebp } from './a-webp.js';
import { notify } from './notify.js';

const CLAVE_GUARDADAS = 'data-company-fotos-guardadas';

/** Lo que se puede arrastrar necesita saber qué se está arrastrando. */
let arrastrando = null;

function nuevaFicha(datos) {
  return {
    id: datos.id || null,
    url: datos.url || '',
    nombre: datos.nombre || '',
    principal: Boolean(datos.principal),
    fichero: datos.fichero || null,
    // Mientras se convierte a WebP: la tarjeta ya se ve -con su foto- pero no
    // se puede enviar todavia. Sin esto, dar a guardar deprisa mandaria el
    // original de veinticinco megas, que es justo lo que se esta evitando.
    preparando: Boolean(datos.preparando),
    // Se revoca al quitarla: cada `createObjectURL` retiene el fichero en
    // memoria hasta que se suelta.
    creada: Boolean(datos.creada),
  };
}

class Fotos {
  constructor(raiz) {
    this.raiz = raiz;
    this.campo = raiz.querySelector('input[type="file"]');
    this.plan = raiz.querySelector('input[type="hidden"][name$="_plan"]');
    this.rejilla = raiz.querySelector('[data-company-fotos-rejilla]');
    this.maximo = parseInt(raiz.getAttribute('data-company-fotos-maximo') || '0', 10) || Infinity;
    // Lo que el servidor admite por foto, en bytes. Se comprueba **antes** de
    // decodificar: mandar el navegador a abrir un fichero enorme que el
    // servidor iba a rechazar es colgar la pestaña para nada.
    this.peso = parseInt(raiz.getAttribute('data-company-fotos-peso') || '0', 10) || Infinity;

    if (!this.campo || !this.rejilla) return;

    this.fichas = this.guardadas();
    this.quitadas = [];
    this.convirtiendo = 0;

    this.campo.addEventListener('change', () => this.anadir());
    this.pintar();
  }

  /**
   * Las que ya estaban, del `<template>`.
   *
   * En un `<template>` y no en atributos sueltos: los nombres llevan comillas y
   * tildes, y ahí dentro no hay forma de que se conviertan en marcado.
   */
  guardadas() {
    const bloque = this.raiz.querySelector(`template[${CLAVE_GUARDADAS}]`);

    if (!bloque) return [];

    try {
      const leidas = JSON.parse(bloque.innerHTML.trim() || '[]');

      return Array.isArray(leidas) ? leidas.map(nuevaFicha) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Las recien elegidas: entran en la rejilla y se convierten a WebP.
   *
   * La tarjeta aparece **ya**, con la vista previa del original, y la
   * conversion va por detras. Esperar a convertir para pintar dejaria el boton
   * pulsado y la pantalla quieta unos segundos con ocho fotos, que se lee como
   * que no funciono.
   *
   * Mientras hay alguna a medias el envio esta bloqueado: la gracia de convertir
   * aqui es que no viaje el original, y guardar deprisa lo mandaria igual.
   */
  anadir() {
    const elegidas = Array.prototype.slice.call(this.campo.files || []);

    elegidas.forEach((fichero) => {
      if (fichero.type.indexOf('image/') !== 0) return;
      if (this.fichas.length >= this.maximo) return;
      if (fichero.size > this.peso) return this.avisarDelPeso(fichero);

      const ficha = nuevaFicha({
        url: URL.createObjectURL(fichero),
        nombre: fichero.name,
        fichero,
        creada: true,
        preparando: true,
        // La primera de todas manda, aunque sea nueva: si no hay ninguna
        // guardada, alguien tiene que ser la principal.
        principal: this.fichas.length === 0,
      });

      this.fichas.push(ficha);
      this.convertir(ficha);
    });

    this.pintar();
  }

  /**
   * Convierte una ficha a WebP y la deja lista.
   *
   * @param {object} ficha
   */
  convertir(ficha) {
    this.convirtiendo += 1;
    this.marcarEnvio();

    aWebp(ficha.fichero)
      .then((convertido) => {
        // Puede haberla quitado mientras tanto: entonces no se toca nada, que
        // volver a meterla en la rejilla seria resucitar lo que ya descarto.
        if (this.fichas.indexOf(ficha) === -1) return;

        ficha.fichero = convertido;
        ficha.nombre = convertido.name;
      })
      .catch(() => {
        // Lo convierte el servidor. No es un fallo que haya que contar.
      })
      .then(() => {
        ficha.preparando = false;
        this.convirtiendo -= 1;
        this.marcarEnvio();
        this.pintar();
      });
  }

  /**
   * Bloquea o suelta el envio del formulario segun queden fotos por convertir.
   *
   * Se marcan los botones con un atributo propio para soltar **solo** los que
   * bloqueo este componente: un boton que ya estaba deshabilitado por otra
   * razon tiene que seguir estandolo.
   */
  marcarEnvio() {
    const formulario = this.raiz.closest('form');

    if (!formulario) return;

    const espera = this.convirtiendo > 0;
    const aviso = this.raiz.querySelector('[data-company-fotos-preparando]');

    if (aviso) aviso.hidden = !espera;

    formulario
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((boton) => {
        if (espera) {
          if (boton.disabled) return;

          boton.disabled = true;
          boton.setAttribute('data-company-fotos-espera', '');

          return;
        }

        if (!boton.hasAttribute('data-company-fotos-espera')) return;

        boton.disabled = false;
        boton.removeAttribute('data-company-fotos-espera');
      });
  }

  /**
   * Dice que una foto no cabe, con el mismo aviso que usa el resto del tema.
   *
   * @param {File} fichero
   */
  avisarDelPeso(fichero) {
    const texto =
      '«' + fichero.name + '» pesa más de lo que admite el servidor. Reduce la foto e inténtalo otra vez.';

    notify.error(texto);
  }

  quitar(indice) {
    const ficha = this.fichas[indice];
    if (!ficha) return;

    if (ficha.creada) URL.revokeObjectURL(ficha.url);
    // Las guardadas hay que decirle al servidor que se van; las nuevas basta
    // con no mandarlas.
    if (ficha.id) this.quitadas.push(ficha.id);

    this.fichas.splice(indice, 1);

    // Si se fue la principal, la primera que quede lo hereda: dejar la ficha
    // sin ninguna marcada haría que los listados dejaran de enseñar foto
    // teniéndola.
    if (ficha.principal && this.fichas.length) {
      this.fichas.forEach((otra, i) => { otra.principal = i === 0; });
    }

    this.pintar();
  }

  hacerPrincipal(indice) {
    this.fichas.forEach((ficha, i) => { ficha.principal = i === indice; });
    this.pintar();
  }

  mover(desde, hasta) {
    if (desde === hasta || desde < 0 || hasta < 0) return;

    const [ficha] = this.fichas.splice(desde, 1);
    this.fichas.splice(hasta, 0, ficha);
    this.pintar();
  }

  /**
   * Rehace el `FileList` del input con las nuevas, en el orden de la rejilla.
   *
   * `DataTransfer` es la única forma de escribir un `FileList`: la propiedad
   * `files` es de solo lectura y su contenido no se puede tocar a mano. Sin
   * esto, quitar una foto la borraría de la pantalla y **se subiría igual**.
   */
  rehacerFicheros() {
    if (typeof DataTransfer !== 'function') return;

    const paquete = new DataTransfer();

    this.fichas.forEach((ficha) => {
      if (ficha.fichero) paquete.items.add(ficha.fichero);
    });

    this.campo.files = paquete.files;
  }

  escribirPlan() {
    if (!this.plan) return;

    let nueva = 0;
    const orden = [];
    let principal = null;

    this.fichas.forEach((ficha) => {
      const clave = ficha.id || 'nueva-' + nueva++;
      orden.push(clave);

      if (ficha.principal) principal = clave;
    });

    this.plan.value = JSON.stringify({
      orden,
      principal,
      quitar: this.quitadas,
    });
  }

  pintar() {
    this.rejilla.textContent = '';

    this.fichas.forEach((ficha, indice) => {
      this.rejilla.appendChild(this.tarjeta(ficha, indice));
    });

    this.rehacerFicheros();
    this.escribirPlan();

    const lleno = this.fichas.length >= this.maximo;
    const boton = this.raiz.querySelector('[data-company-fotos-elegir]');

    if (boton) boton.hidden = lleno;

    const aviso = this.raiz.querySelector('[data-company-fotos-lleno]');

    if (aviso) aviso.hidden = !lleno;
  }

  tarjeta(ficha, indice) {
    const suelta = document.createElement('figure');
    suelta.className =
      'owari-foto' +
      (ficha.principal ? ' owari-foto-principal' : '') +
      (ficha.preparando ? ' owari-foto-preparando' : '');
    // No se arrastra lo que aun se esta convirtiendo: al terminar se repinta la
    // rejilla, y un arrastre a medias acabaria soltandola en otro sitio.
    suelta.draggable = !ficha.preparando;
    suelta.setAttribute('data-indice', String(indice));

    const imagen = document.createElement('img');
    imagen.src = ficha.url;
    // El nombre por `alt` y nunca dentro del HTML: lo escribe quien sube.
    imagen.alt = ficha.nombre;
    imagen.loading = 'lazy';
    suelta.appendChild(imagen);

    // La X. Un `<button>` y no un icono a secas: se llega con el tabulador y
    // se pulsa con el teclado, que es la diferencia entre poder quitarla y no.
    const quitar = document.createElement('button');
    quitar.type = 'button';
    quitar.className = 'owari-foto-quitar';
    quitar.setAttribute('aria-label', 'Quitar ' + ficha.nombre);
    quitar.textContent = '×';
    quitar.addEventListener('click', () => this.quitar(indice));
    suelta.appendChild(quitar);

    const marca = document.createElement('button');
    marca.type = 'button';
    marca.className = 'owari-foto-marca';
    marca.setAttribute('aria-pressed', ficha.principal ? 'true' : 'false');
    marca.setAttribute(
      'aria-label',
      ficha.principal ? ficha.nombre + ' es la principal' : 'Hacer principal ' + ficha.nombre
    );
    marca.textContent = ficha.principal ? 'Principal' : 'Hacer principal';
    marca.addEventListener('click', () => this.hacerPrincipal(indice));
    suelta.appendChild(marca);

    suelta.addEventListener('dragstart', (evento) => {
      arrastrando = { fotos: this, indice };
      suelta.classList.add('owari-foto-arrastrando');

      if (evento.dataTransfer) evento.dataTransfer.effectAllowed = 'move';
    });

    suelta.addEventListener('dragend', () => {
      arrastrando = null;
      suelta.classList.remove('owari-foto-arrastrando');
    });

    // Solo dentro del mismo componente: dos rejillas en la misma pantalla -la
    // ficha de producto lleva la de su primera referencia- no se mezclan.
    suelta.addEventListener('dragover', (evento) => {
      if (!arrastrando || arrastrando.fotos !== this) return;

      evento.preventDefault();
      suelta.classList.add('owari-foto-encima');
    });

    suelta.addEventListener('dragleave', () => suelta.classList.remove('owari-foto-encima'));

    suelta.addEventListener('drop', (evento) => {
      if (!arrastrando || arrastrando.fotos !== this) return;

      evento.preventDefault();
      suelta.classList.remove('owari-foto-encima');
      this.mover(arrastrando.indice, indice);
    });

    return suelta;
  }
}

/**
 * @param {Document|Element} [raiz]
 */
export function setupFotos(raiz = document) {
  raiz.querySelectorAll('[data-company-fotos]').forEach((elemento) => {
    if (elemento.dataset.companyFotosListo) return;
    elemento.dataset.companyFotosListo = '1';

    const fotos = new Fotos(elemento);
    const boton = elemento.querySelector('[data-company-fotos-elegir]');
    const campo = elemento.querySelector('input[type="file"]');

    // El botón de verdad es este; el input queda escondido y solo se le pide
    // que abra el diálogo. Así el aspecto es el del tema y no el que pinte
    // cada navegador, que es distinto en cada uno.
    if (boton && campo) boton.addEventListener('click', () => campo.click());

    elemento.__owariFotos = fotos;
  });
}
